'use client';

import React, { createContext, useCallback } from 'react';
import { usePathname } from 'next/navigation';

import { RouteObject } from '~/app/types';
import { useMenuRoutes } from '~/shared/components/Nav/hooks/useMenuRoutes';

type NavContextData = {
  isAppMenuOpen: boolean;
  subMenuOpen?: (null | RouteObject)[];
  menuItems: RouteObject[];
  footerItems: RouteObject[];
  toggleAppMenu: () => void;
  openAppMenu: () => void;
  closeAppMenu: () => void;
  scheduleOpenAppMenu: () => void;
  scheduleCloseAppMenu: () => void;
  keepAppMenuOpen: () => void;
  toggleSubMenu: (item: RouteObject | null, isRootLevel?: boolean) => void;
  checkIfIsActive: (menuRoute: string, subitems?: RouteObject[]) => boolean;
};

const NavContext = createContext({} as NavContextData);

const MENU_OPEN_DELAY = 180;
const MENU_CLOSE_DELAY = 400;

type NavProviderProps = {
  children: React.ReactNode;
};

export function NavProvider({ children }: NavProviderProps) {
  const pathname = usePathname();

  const [isAppMenuOpen, setIsAppMenuOpen] = React.useState(false);
  const { menuRoutes, footerRoutes } = useMenuRoutes();
  const [subMenuOpen, setSubmenuOpen] = React.useState<(RouteObject | null)[]>(
    []
  );

  const toggleSubMenu = (item: RouteObject | null, isRootLevel?: boolean) => {
    setSubmenuOpen(oldValue => {
      if (!item) {
        return [];
      }
      if (isRootLevel) {
        return [item];
      }
      return [...oldValue, item];
    });
  };

  const checkIfIsActive = useCallback(
    (menuRoute: string, subitems?: RouteObject[]) => {
      const hasSomeSubitemActive = subitems?.some(
        subitem => subitem.path === pathname
      );

      return (!!menuRoute && pathname === menuRoute) || !!hasSomeSubitemActive;
    },
    [pathname]
  );

  function toggleAppMenu() {
    setIsAppMenuOpen(state => !state);
  }

  const openAppMenu = useCallback(() => setIsAppMenuOpen(true), []);

  const closeAppMenu = useCallback(() => {
    setIsAppMenuOpen(false);
    setSubmenuOpen([]);
  }, []);

  const openTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  }, []);

  React.useEffect(() => clearTimers, [clearTimers]);

  const scheduleOpenAppMenu = useCallback(() => {
    clearTimers();
    openTimer.current = setTimeout(openAppMenu, MENU_OPEN_DELAY);
  }, [clearTimers, openAppMenu]);

  const scheduleCloseAppMenu = useCallback(() => {
    clearTimers();
    closeTimer.current = setTimeout(closeAppMenu, MENU_CLOSE_DELAY);
  }, [clearTimers, closeAppMenu]);

  const keepAppMenuOpen = useCallback(() => {
    clearTimers();
    openAppMenu();
  }, [clearTimers, openAppMenu]);

  const providerValue = React.useMemo(
    () => ({
      isAppMenuOpen,
      subMenuOpen,
      menuItems: menuRoutes,
      footerItems: footerRoutes,
      toggleAppMenu,
      openAppMenu,
      closeAppMenu,
      scheduleOpenAppMenu,
      scheduleCloseAppMenu,
      keepAppMenuOpen,
      toggleSubMenu,
      checkIfIsActive,
    }),
    [
      isAppMenuOpen,
      subMenuOpen,
      menuRoutes,
      footerRoutes,
      checkIfIsActive,
      openAppMenu,
      closeAppMenu,
      scheduleOpenAppMenu,
      scheduleCloseAppMenu,
      keepAppMenuOpen,
    ]
  );

  return (
    <NavContext.Provider value={providerValue}>{children}</NavContext.Provider>
  );
}

export function useNav() {
  const context = React.useContext(NavContext);

  if (!context) {
    throw new Error('useNav must be used within a NavProvider');
  }

  return context;
}
